// Dependencies
import moment from 'moment';
import { LogManagerConsole } from 'Core/logManager';
import { getMessage } from '../../../../core/hooks'

/**
 * Builds a summary for the fuel card alert.
 * @param {string} event - The alert event name.
 * @param {Object} messages - The internacionalization messages object.
 * @returns {string} - The Alert event text.
 */
export const fuelCardSummary = (event, messages) => {
  const alertEvents = {
    NotAtLocation: messages['alertsNewAlert_NotAtLocation'],
    Overfill: messages['alertsNewAlert_Overfill'],
  };

  return alertEvents[event];
};

/**
 * Builds a summary for an alert with slider (e.g. Over 7 Days).
 * @param {Object} triggerDuration - The alert trigger duration.
 * @param {Object} messages - The internacionalization messages object.
 */
export const overSliderSummary = (triggerDuration, messages) => {
  const { duration, value } = triggerDuration;
  const tense = value === 1 ? duration.slice(0, -1) : duration;
  const tense_intl =  messages['alertsNewAlert_' + tense?.toLowerCase()]

  return value === 0
    ? messages['alertsNewAlert_immediate']
    : `${messages['alertsNewAlert_StoppedReportingOverSummary']} ${value} ${tense_intl}`;
};

/**
 * Builds a summary for alerts with multi-select events (behavior and off hour).
 * @param {Array} eventsArray - List of events of the driver behavior alert.
 * @param {Object} messages - The internacionalization messages object.
 * @returns {string} - The Alert summary text.
 */
export const multiselectEventsSummary = (eventsArray, messages) => {
  const summaryLabel = {
    PotentialIncident: messages['alertsNewAlert_PotentialIncident'],
    HardStop: messages['alertsNewAlert_HardStop'],
    HardTurn: messages['alertsNewAlert_HardTurn'],
    RapidAcceleration: messages['alertsNewAlert_RapidAcceleration'],
    Ignition: messages['alertsNewAlert_Ignition'],
    Drive: messages['alertsNewAlert_Drive'],
    Input: messages['alertsNewAlert_Input'],
    Geofence: messages['alertsNewAlert_Geofence'],
    Landmark: messages['alertsNewAlert_Landmark'],
  }

  return eventsArray.map((event) => summaryLabel[event]).toString();
};


/**
 * Builds a summary for alerts with list of multi-select events (Routes Watcher).
 * @param {Array} eventsArray - List of events of the Routes (Watchers) alert.
 * @param {Object} messages - The internacionalization messages object.
 * @returns {string} - The Alert summary text.
 */
export const multiselectListEventsSummary= (eventsArray, messages) => {
  return eventsArray?.map((event, index) => { 
    return (
      (index == 0 ? '' : ' ') + (getMessage(`alertEvent_${event}`, messages) || "") + (index === eventsArray?.length - 1 ? '' : ',')
    )
  });
}

/**
 * Builds a summary for alerts with list of multi-select events (Routes Watcher).
 * @param {Array} eventsArray - List of events of the Routes (Watchers) alert.
 * @param {Object} messages - The internacionalization messages object.
 * @returns {string} - The Alert summary text.
 */
export const multiselectListEventsSummaryRoute = (eventsArray, messages) => {
  const summaryLabel = {
    RouteDeviation: messages['alertsNewAlert_RouteWatchers_RouteDeviation'],
    StartRoute: messages['alertsNewAlert_RouteWatchers_startRoute'],
    EndRoute: messages['alertsNewAlert_RouteWatchers_endRoute']
  }
  return eventsArray?.map((event, index) => { 
    return (
      (index == 0 ? '' : ' ') + summaryLabel[event] + (index === eventsArray.length - 1 ? '' : ',')
    )
  });
};


/**
 * Builds a summary for the speed alert.
 * @param {Object} alertConfigSpeed 
 * @param {Object} intl - Internacionalization object.
 * @param {string} speedMeasure - The speed unit.
 * @param {boolean} whileWhithinLandMarks - True if a trigger is pass over a landmark.
 * @returns {string} The built summary.
 */
export const speedSummary = (alertConfigSpeed, intl, speedMeasure, whileWhithinLandMarks, isKph = false) => {
  let { triggerSpeed, triggerPostedSpeed, operatorAnd } = alertConfigSpeed;
  LogManagerConsole("values", "triggerSpeed, triggerPostedSpeed, operatorAnd")
  const { messages } = intl;
  const operatorAndOr = (operatorAnd == 'True' ? messages['alertsNewAlert_SpeedSummarySpeedMessageAnd'] : messages['alertsNewAlert_SpeedSummarySpeedMessageOr'])

  const getOdometerUnitValue = (value) => {

    if (!value)
      return value

    value = Number(value)

    if (value == 0)
      return 0

    if (value && isKph)
      value = (value * 1.609)

    return Math.round(value) || 0
  }

  triggerSpeed = getOdometerUnitValue(triggerSpeed)
  triggerPostedSpeed = getOdometerUnitValue(triggerPostedSpeed)

  const speedMessage = intl.formatMessage(
    { id: 'alertsNewAlert_SpeedSummarySpeedMessage' },
    {
      speed: triggerSpeed,
      postedSpeed: triggerPostedSpeed,
      speedMeasure,
    },
  );
  const postedSpeedMessage = intl.formatMessage(
    { id: 'alertsNewAlert_SpeedSummaryPostedSpeedMessage' },
    {
      postedSpeed: triggerPostedSpeed,
      speedMeasure,
    },
  );
  //const insideLandmarkText = intl.messages["alertsNewAlert_SpeedSummaryInsideLandmark"];

  return `${triggerSpeed > 0 ? speedMessage : ''} ${triggerSpeed > 0 && Number.isInteger(triggerPostedSpeed) && triggerPostedSpeed > 0 ? operatorAndOr : ''} 
  ${Number.isInteger(triggerPostedSpeed) && triggerPostedSpeed > 0 ? postedSpeedMessage : ''}`;
  //${whileWhithinLandMarks ? insideLandmarkText : ''};
};

/**
 * Builds a summary for the on event alert.
 * @param {Object} alertConfigOnEvent - The alert config options.
 * @param {Object} intl - Internacionalization object.
 * @returns {string} The built summary.
 */
export const onEventSummary = (alertConfigOnEvent, intl) => {
  const { event, inputs } = alertConfigOnEvent;
  const { messages } = intl;
  const inputEventOptions = {
    1: messages['alertsNewAlert_Input1'],
    2: messages['alertsNewAlert_Input2'],
    3: messages['alertsNewAlert_Input3'],
    4: messages['alertsNewAlert_Input4'],
    5: messages['alertsNewAlert_Input5'],
  };

  const onEventOptions = {
    MainPowerOff: messages['alertsNewAlert_MainPowerOff'],
    MainPowerOn: messages['alertsNewAlert_MainPowerOn'],
    MainPowerDisconnect: messages['alertsNewAlert_MainPowerDisconnect'],
    Input: messages['alertsNewAlert_Input'],
    AntennaDisconnect: messages['alertsNewAlert_AntennaDisconnect'],
    AntennaConnect: messages['alertsNewAlert_AntennaConnect'],
    InternalBatteryDisconnect: messages['alertsNewAlert_InternalBatteryDisconnect'],
    InternalBatteryConnect: messages['alertsNewAlert_InternalBatteryConnect'],
    Update: messages['alertsNewAlert_Update'],
    IgnitionOn: messages['alertsNewAlert_IgnitionOn'],
    IgnitionOff: messages['alertsNewAlert_IgnitionOff'],
    Ping: messages['alertsNewAlert_Ping'],
    Motion: messages['alertsNewAlert_Motion'],
    Drive: messages['alertsNewAlert_Drive'],
    LowBattery: messages['alertsNewAlert_LowBattery'],
    TamperSensor: messages['alertsNewAlert_TamperSensor'],
    UserUpdate: messages['alertsNewAlert_UserUpdate'],
    LightSensor: messages['alertsNewAlert_LightSensor'],
  };

  const inputsLabels = inputs?.map((input) => inputEventOptions[input]).toString();
  const eventInputs = intl.formatMessage(
    { id: 'alertsNewAlert_OnEventSummaryEventWithInputs' },
    { inputs: inputsLabels },
  );

  return `${inputs?.length > 0 ? eventInputs : onEventOptions[event] || ""}`;
}

/**
 * Builds a summary for stop alert (e.g. Over 7 Days).
 * @param {Object} triggerDuration - The alert trigger duration.
 * @param {Object} intl - The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const stopSummary = (triggerDuration, intl) => {
  const { duration, value } = triggerDuration;
  const { messages } = intl;
  const tense = value === 1 ? duration.slice(0, -1) : duration;
  const tense_intl = messages['alertsNewAlert_' + tense?.toLowerCase()]

  const immediateTriggerMessage = intl.formatMessage(
    { id: 'alertsNewAlert_StopSummary' },
    { trigger: messages['alertsNewAlert_immediate'] },
  );

  const timeTriggerMessage = intl.formatMessage(
    { id: 'alertsNewAlert_StopSummary' },
    { trigger: ` ${value} ${tense_intl}` },
  );

  return value === 0
    ? immediateTriggerMessage
    : timeTriggerMessage;
};

/**
 * Builds a summary for vehicle congregation alert (e.g. 4 units allowed in landmarks: one,two,three).
 * @param {Object} maximumUnits - Maximun allowed units.
 * @param {Object} intl - The internacionalization messages object.
 * @param {Array} triggers - The selected landmarks or landmarkGroups.
 * @returns {string} The built summary.
 */
export const vehicleCongregationSummary = (maximumUnits, intl, triggers) => {
  const { messages } = intl;
  const selectedLandmarks = triggers?.map((trigger) => trigger.entity.label);
  const landmarkEntityTitle = triggers[0]?.entity?.name;
  const landmarkEntityName = {
    LandmarkGroup: messages['alertsNewAlert_LandmarkGroup'],
    Landmark: messages['alertsNewAlert_Landmark'],
  }
  const allowedMessage = intl.formatMessage(
    { id: 'alertsNewAlert_VehicleCongregationAllowed' },
    { allowedUnits: maximumUnits },
  );

  const landmarkMessage = intl.formatMessage(
    { id: 'alertsNewAlert_VehicleCongregationLandmark' },
    {
      landmarkEntityTitle: `${landmarkEntityName[landmarkEntityTitle]}(s)`,
      landmarks: selectedLandmarks.toLocaleString(),
    },
  );

  return `${allowedMessage} ${triggers?.length !== 0 ? landmarkMessage : ''}`
};

/**
 * Builds a summary for media alert (e.g. Trigger when: Panic Button).
 * @param {Object} alertConfigMedia - The alert media config.
 * @param {Object} intl - The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const mediaSummary = (alertConfigMedia, intl) => {
  const { messages } = intl;
  const { impactDetected, panicButton } = alertConfigMedia;
  const bothTriggers = intl.formatMessage(
    { id: 'alertsNewAlert_MediaSummaryBothTriggers' },
    {
      triggerOne: messages['alertsNewAlert_impactDetectedLabel'],
      triggerTwo: messages['alertsNewAlert_panicButtonLabel'],
    });
  const trigger = {
    impactDetected: messages['alertsNewAlert_impactDetectedLabel'],
    panicButton: messages['alertsNewAlert_panicButtonLabel']
  };
  const triggers = impactDetected && panicButton
    ? bothTriggers
    : trigger[impactDetected ? 'impactDetected' : 'panicButton'];

  return intl.formatMessage(
    { id: 'alertsNewAlert_MediaSummary' },
    { trigger: triggers }
  );
};

/**
 * Builds a summary for consecutive driving alert (e.g. 3 Hours).
 * @param {Object} drivingMinutesExceeded - The driving Hours Exceeded limit.
 * @param {Object} messages The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const consecutiveDrivingSummary = (drivingMinutesExceeded, messages) => {
  let hours = Math.floor(drivingMinutesExceeded / 60);
  let mins = Math.floor(drivingMinutesExceeded - hours * 60);
  return `${hours} ${messages['Hours']} ${mins} ${messages['mins']} ${messages['alertsNewAlert_drivingHoursExceededLabel']}`
}

/**
 * Builds a summary for FSMMealBreak alert
 * @param {Object} config - The driving Hours Exceeded limit.
 * @param {Object} messages The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const fsmMealBreakSummary = (config, messages) => {
  return `${messages['alertsNewAlert_notifyAt']}: ${config?.triggerDuration?.value} ${messages['Hours']} - ${messages['alertsNewAlert_breakDuration']}: ${config.breakDurationMinutes} ${messages['mins']}`
}

/**
 * Builds a summary for Idle alert (e.g. Trigger: 10 Hours).
 * @param {Object} configJSON The Idle alert config.
 * @param {Object} intl - The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const idleSummary = (configJSON, intl) => {
  const { triggerDuration: { duration, value }, allowIdlingWhenPTOOn } = configJSON;
  const { messages } = intl;
  const tense = value === 1 ? duration.slice(0, -1) : duration;
  let message;
  if(tense == "Minutes") {
    message = `${value} ${messages['alertsNewAlert_minutesLabel']}`
  }
  else if(tense == "Hours") {
    message = `${value} ${messages['alertsNewAlert_hoursLabel']}`
  }
   

  return intl.formatMessage(
    { id: 'alertsNewAlert_IdleSummary' },
    {
      trigger: message, IdlingAllowed: allowIdlingWhenPTOOn
        ? messages['alertsNewAlert_IdleSummaryAllowedIdling']
        : messages['alertsNewAlert_IdleSummaryNotAllowedIdling']
    }
  );
};

/**
 * Builds a summary for Total drive time alert 
 * (e.g. From 01:00 AM to 06:00 AM, 6 driving exceeded hours.).
 * @param {Object} alertConfigDriveTime - The Total drive time alert config.
 * @param {Object} intl - The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const totalDriveTimeSummary = (alertConfigDriveTime, intl) => {
  const { startWindowTime, endWindowTime, drivingHoursExceeded } = alertConfigDriveTime;
  const formatTime = (minutes) => {
    const hours = minutes / 60 | 0;
    const mins = minutes % 60 | 0;

    return moment.utc().hours(hours).minutes(mins).format("hh:mm A");
  }
  const startTime = formatTime(startWindowTime);
  const endTime = formatTime(endWindowTime);

  return intl.formatMessage(
    { id: 'alertsNewAlert_TotalDriveTimeSummary' },
    { startTime, endTime, drivingHoursExceeded },
  );
};

/**
 * Builds a summary for Landmark alert.
 * @param {Object} triggers - The Landmark alert selected landmarks or landmark groups.
 * @param {Object} alertConfigLandMark - The landmark alert config object.
 * @param {Object} intl - The internacionalization messages object.
 * @returns {string} The built summary.
 */
export const landmarkSummary = (triggers, trigger, intl) => {
  const { messages } = intl;

  if (triggers) {
    const landmarks = triggers.map((trigger) => {
      return trigger.entity.label;
    });

    const landmarkEntityId = triggers[0]?.entity.name;
    const landmarkEntityName = {
      Tag: messages['alertsNewAlert_Tag'],
      Landmark: messages['alertsNewAlert_Landmark'],
    };

    const triggersMessage = () => {
      if (triggers.length === 0) { // If no landmarks selected.
        return messages['alertsNewAlert_LandmarksSummaryNoLandmarks'];
      } else {
        if (triggers[0].id == '-1') { // If All landmarks selected.
          return messages['alertsNewAlert_LandmarksSummaryAllLandmarks'];
        } else {
          return intl.formatMessage(
            { id: 'alertsNewAlert_LandmarksSummary' },
            {
              trigger: messages['alertsNewAlert_' + trigger?.toLowerCase()],
              landmarks: landmarks.toString(),
              landmarkEntity: landmarkEntityName[landmarkEntityId],
            }
          );
        }
      }
    }
    return triggersMessage();
  }

  return null;
};


export const geofenceSummary = (triggers, trigger, intl) => {
  const { messages } = intl;
  const geofence = triggers.map((trigger) => {
    return trigger.entity.label;
  });
  const entityId = triggers[0]?.entity.name;
  const entityName = {
    Tag: messages['alertsNewAlert_Tag'],
    Geofence: messages['alertsNewAlert_Geofence'],
  };

  const triggersMessage = () => {
    if (triggers.length === 0) { // If no landmarks selected.
      return messages['alertsNewAlert_GeofencesSummaryNoLandmarks'];
    } else {
      if (triggers[0].id == '-1') { // If All landmarks selected.
        return messages['alertsNewAlert_GeofencesSummaryNoLandmarks'];
      } else {
        return intl.formatMessage(
          { id: 'alertsNewAlert_GeofencesSummary' },
          {
            trigger,
            geofences: geofence.toString(),
            geofenceEntity: entityName[entityId],
          }
        );
      }
    }
  }

  return triggersMessage();
};


const getIdleSummary = (alert, intl) => {
  const { configJSON } = alert;
  const parsedConfigJson = JSON.parse(configJSON);

  return idleSummary({ ...parsedConfigJson }, intl);
};

const getSmartOneCLowBattery = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_SmartOneCLowBattery'];
};

const getConsistentSpeed = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_ConsistentSpeed'];
};

const getEmissionCheck = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_EmissionCheck'];
};

const getLightDutyDTC = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_LightDutyDTC'];
};

const getHeavyDutyDTC = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_HeavyDutyDTC'];
};

const getTow = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_Tow'];
};

const getUnassignedDriver = (alert, intl) => {
  const { messages } = intl;
  return messages['alertsNewAlert_unassignedDriver'];
};

const getSpeedSummary = (alert, intl, user) => {
  const { configJSON, triggers } = alert;
  const parsedConfigJson = JSON.parse(configJSON);
  const whileWhithinLandMarks = triggers?.length;

  return speedSummary(parsedConfigJson, intl, user?.isKph ? "KPH" : "MPH", whileWhithinLandMarks, user?.isKph);
};

const getStopSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { triggerDuration } = JSON.parse(configJSON);

  return stopSummary(triggerDuration, intl);
};

const getMediaSummary = (alert, intl) => {
  const { configJSON } = alert;
  const alertConfigMedia = JSON.parse(configJSON);

  return mediaSummary(alertConfigMedia, intl);
};

const getLandmarkSummary = (alert, intl) => {
  const { configJSON, triggers } = alert;
  const { trigger } = JSON.parse(configJSON);

  return landmarkSummary(triggers, trigger, intl);
};

const getGeofenceSummary = (alert, intl) => {
  const { configJSON, triggers } = alert;
  const { trigger } = JSON.parse(configJSON);

  return geofenceSummary(triggers, trigger, intl);
};

const getVehicleCongregationSummary = (alert, intl) => {
  const { configJSON, triggers } = alert;
  const { maximumUnits } = JSON.parse(configJSON);

  return vehicleCongregationSummary(maximumUnits, intl, triggers || []);
};

const getFuelCardSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { fuelCardEvent } = JSON.parse(configJSON);

  return fuelCardSummary(fuelCardEvent, intl.messages);
};

const getStoppedReportingSummary = (alert, intl) => {
  const { messages } = intl;
  const { configJSON } = alert;
  const { triggerDuration, cameraTriggerDuration } = JSON.parse(configJSON);
  const { duration, value } = triggerDuration;
  const durationCamera = cameraTriggerDuration?.duration ? cameraTriggerDuration.duration : messages['Hours']
  const valueCamera = cameraTriggerDuration?.value ? cameraTriggerDuration.value : 1
  const tense = value === 1 ? duration.slice(0, -1) : duration;
  const tenseCamera = valueCamera === 1 ? durationCamera.slice(0, -1) : durationCamera;
  if (!cameraTriggerDuration?.duration)
    return stopSummary(triggerDuration, intl);
  const immediateTriggerMessage = intl.formatMessage({ id: 'alertsNewAlert_StopReportingSummary' }, { trigger: 'Immediate', cameraTrigger: 'Immediate' });
  const timeTriggerMessage = intl.formatMessage({ id: 'alertsNewAlert_StopReportingSummary' }, { trigger: ` ${value} ${traslateDates(tense, messages)}`, cameraTrigger: ` ${valueCamera} ${traslateDates(tenseCamera, messages)}` });
  return value === 0 ? immediateTriggerMessage : timeTriggerMessage;
};

const getStationarySummary = (alert, intl) => {
  const { configJSON } = alert;
  const { triggerDuration } = JSON.parse(configJSON);

  return overSliderSummary(triggerDuration, intl.messages);
};

const getDriverBehaviorSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { driverBehaviorEvents } = JSON.parse(configJSON);

  return multiselectEventsSummary(driverBehaviorEvents, intl.messages);
};

const getOffHourSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { offHourEvents } = JSON.parse(configJSON);

  return multiselectEventsSummary(offHourEvents, intl.messages);
};

const getOnEventSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { event, inputs } = JSON.parse(configJSON);

  return onEventSummary({ event, inputs }, intl) || "";
};

const getConsecutiveDrivingSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { drivingMinutesExceeded } = JSON.parse(configJSON);

  return consecutiveDrivingSummary(drivingMinutesExceeded, intl.messages);
};

const getTotalDriveTimeSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { startWindowTime, endWindowTime, drivingHoursExceeded } = JSON.parse(configJSON);
  const alertConfigDriveTime = { startWindowTime, endWindowTime, drivingHoursExceeded };

  return totalDriveTimeSummary(alertConfigDriveTime, intl);
};

const getCameraEventSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { cameraEvents } = JSON.parse(configJSON);
  
  return multiselectListEventsSummary(cameraEvents, intl.messages);
};

const getFSMMealBreakSummary = (alert, intl) => {
  const { configJSON } = alert;
  const config = JSON.parse(configJSON);
  return fsmMealBreakSummary(config, intl.messages) || "";
};

const getRoutesWatcherSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { routeWatchers } = JSON.parse(configJSON);

  return multiselectListEventsSummaryRoute(routeWatchers, intl.messages);
};

const getFuelLevel = (alert, intl) => {
  const { messages } = intl;
  const { configJSON } = alert;
  const { dropping, raising } = JSON.parse(configJSON);
  return `${dropping > 0 ? `${messages['alertsNewAlert_dropping']} ${dropping}` : '' }${dropping > 0 && raising > 0 ? `,`: ''} ${raising > 0 ? `${messages['alertsNewAlert_raising']} ${raising}` : ''}`;
};

const getWarningSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { messages } = intl;

  const config = JSON.parse(configJSON);
  if (config.warnings.length) {
    const { warnings } = config
    let labels = warnings.map((item) => (messages[`alertsNewAlert_${item}`] || item))
    return labels.join(', ')
  }

  return "";
}

const getInspectionSummary = (alert, intl) => {
  const { configJSON } = alert;
  const { messages } = intl;
  const config = JSON.parse(configJSON);
  const { triggerType, triggerStatus, triggerDefects, triggerDuration } = config;

  // Inspection Status: Incomplete Inspection, Unverified Inspection
  if (triggerType === 'InspectionStatus' && triggerStatus?.length) {
    const labels = triggerStatus.map((status) => (messages[`alertsNewAlert_${status}Inspection`] || status));
    return labels.join(', ');
  }

  // Inspection Defects: Critical Defect, Minor Defect
  if (triggerType === 'InspectionDefects' && triggerDefects?.length) {
    const labels = triggerDefects.map((defect) => (messages[`alertsNewAlert_${defect}Defect`] || defect));
    return labels.join(', ');
  }

  // Inspection Duration: Inspection Under Duration, Inspection Over Duration
  if (triggerType === 'InspectionDuration' && triggerDuration?.thresholdType) {
    const { thresholdType } = triggerDuration;
    return messages[`alertsNewAlert_Inspection${thresholdType}Duration`] || `Inspection ${thresholdType} Duration`;
  }

  return "";
};

export const buildAlertSummary = (alert, intl, user) => {
  if (!alert) return "";

  const { type, configJSON } = alert;
  try {
    JSON.parse(configJSON);
  } catch (error) {
    console.log("error parsing json buildAlertSummary", error);
    return "";
  }

  const alertSummary = {
    Idle: () => getIdleSummary(alert, intl),
    Speed: () => getSpeedSummary(alert, intl, user),
    Stop: () => getStopSummary(alert, intl),
    Media: () => getMediaSummary(alert, intl),
    Landmark: () => getLandmarkSummary(alert, intl),
    Geofence: () => getGeofenceSummary(alert, intl),
    VehicleCongregation: () => getVehicleCongregationSummary(alert, intl),
    FuelCard: () => getFuelCardSummary(alert, intl),
    StoppedReporting: () => getStoppedReportingSummary(alert, intl),
    Stationary: () => getStationarySummary(alert, intl),
    DriverBehavior: () => getDriverBehaviorSummary(alert, intl),
    OffHour: () => getOffHourSummary(alert, intl),
    OnEvent: () => getOnEventSummary(alert, intl),
    ConsecutiveDriving: () => getConsecutiveDrivingSummary(alert, intl),
    TotalDriveTime: () => getTotalDriveTimeSummary(alert, intl),
    HeavyDutyDTC: () => getHeavyDutyDTC(alert, intl),
    ConsistentSpeed: () => getConsistentSpeed(alert, intl),
    SmartOneCLowBattery: () => getSmartOneCLowBattery(alert, intl),
    UnassignedDriver: () => getUnassignedDriver(alert, intl),
    Tow: () => getTow(alert, intl),
    LightDutyDTC: () => getLightDutyDTC(alert, intl),
    CameraEvent: () => getCameraEventSummary(alert, intl),
    //new
    LowBattery: () => '',
    InstallSensor: () => '',
    UserUpdate: () => '',
    TamperSensor: () => '',
    LightSensor: () => '',
    FSMMealBreak: () => getFSMMealBreakSummary(alert, intl),
    RouteWatchers: () => getRoutesWatcherSummary(alert, intl),
    Warning: () => getWarningSummary(alert, intl),
    FuelLevel:() => getFuelLevel(alert, intl),
    EmissionCheck: () => getEmissionCheck(alert, intl),
    Inspection: () => getInspectionSummary(alert, intl)
  };
  return alertSummary[type] ? alertSummary[type]() : "";
};

const traslateDates = (date, messages) => {
  if (date.includes("Day")) {
    return date.replace('Day', messages['Day'])
  } else if (date.includes("Hour")) {
    return date.replace('Hour', messages['Hour'])
  } else {
    return date.replace('Minutes', messages['alertsNewAlert_minutes'])
  }
}